import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub } from "@/components/ui/sidebar";
import { useGetOrganizations } from "@/features/organization/hooks/useQuery/useGetOrganizations";
import useCreateProject from "@/lib/useQuery/project/useCreateProject";
import { useOrganizationStore, useProjectStore, useSurveyStore, useUserStore } from "@/lib/zustand/useStore";
import { Archive, ArrowLeft, ChevronDown, ChevronsUpDown, Eye, EyeOff, FilePlus2, ListChecks, ListTodo, Loader, MoreHorizontal, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { ProjectCrmModal } from "@/features/project/components/organisms/ProjectCrmModal";
import { AddQuestionModal } from '@/features/question/components/organisms/AddQuestionModal';
import { QuestionFormSchemaType as QuestionDataType, QuestionFormSchemaType } from '@/features/question/components/organisms/QuestionForm';
import { useDeleteSurvey } from "@/features/survey/hooks/useMutation/useDeleteSurvey";
import { useGetSurveysByProjectId } from "@/features/survey/hooks/useQuery/useGetSurveysByProjectId";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useDeleteProject } from "@/lib/useQuery/project/useDeleteProject";
import { useProjectsList } from "@/lib/useQuery/project/useProjectsList";
import { cn } from "@/lib/utils";
import { AddProjectModal, type schemaType as AddProjectModalSchemaType } from "@features/project/components/organisms/AddProjectModal";
import { CreateOrganizationModal } from "../../../features/organization/components/organisms/CreateOrganizationModal";
import ErrorPopup from "../../custom/InfoPopups/ErrorPopup";
import SuccessPopup from "../../custom/InfoPopups/SuccessPopup";
import { SIDEBAR_NAMES } from "@/constants/sidebar";
import { BorderBox } from "@/components/atoms/BorderBox";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen";
import { OrganizationTypeWordingResolver } from "@/features/organization/components/atoms/OrganizationTypeWordingResolver";
import { OrganizationFormSchemaType } from "@/features/organization/components/organisms/OrganizationForm";
import { useCreateOrganization } from "@/features/organization/hooks/useMutation/useCreateOrganization";
import { EditQuestionModal } from "@/features/question/components/organisms/EditQuestionModal";
import { useAddQuestion } from "@/features/question/hooks/useMutation/useAddQuestion";
import { useArchiveQuestion } from "@/features/question/hooks/useMutation/useArchiveQuestion";
import { useDeleteQuestion } from "@/features/question/hooks/useMutation/useDeleteQuestion";
import { useRestoreQuestion } from "@/features/question/hooks/useMutation/useRestoreQuestion";
import { QuestionWithDependents, useGetQuestionsBySurveyId } from "@/features/question/hooks/useQuery/useGetQuestionsBySurveyId";
import { CheckProjectAccess } from "@/features/rbac/components/molecules/CheckProjectAccess";
import { useCheckProjectAccess } from "@/features/rbac/hooks/utils/useCheckProjectAccess";
import { projectSortConfig, questionsSortConfig } from "@/hooks/useSortBy/sortConfigs";
import { useSortByWithConfig } from "@/hooks/useSortBy/useSortByWithConfig";
import { ConfirmByTypingTextDialog } from "../ConfirmByTypingTextDialog";
import { useUpdateQuestion } from "@/features/question/hooks/useMutation/useUpdateQuestion";
import { QuestionWithResponses } from "@/features/survey_sync/hooks/useCheckSyncSurveysWithTemplate";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


export function AppSidebar() {
    const { session } = useUserStore();
    const organizationsQuery = useGetOrganizations({ isEnabled: session ? true : false });
    const navigate = useNavigate();

    const { organization, setOrganization } = useOrganizationStore()
    const { setProject } = useProjectStore()

    const {
        isLoading: isFetchingProjectsList,
        data: projects,
        error: projectsListError,
        refetch: refetchProjectsList
    } = useProjectsList({
        orgId: organization?.id,
        sortByCreatedAtAscending: false
    });
    const {
        Icon: ProjectsSortByIcon,
        label: projectsSortByLabel,
        nextOption,
        sortedResult: sortedProjects,
    } = useSortByWithConfig(
        projectSortConfig,
        projects ?? [],
        'created_at_desc'
    );



    const { orgId } = useParams();

    useEffect(() => {
        if (organizationsQuery.data && organizationsQuery.data.length > 0 && !orgId) {
            // setOrganization(organizationsQuery.data[0])
            setOrganization(null);
        } else {
            const org = organizationsQuery.data?.find(org => org.id === Number(orgId!));
            if (org) {
                setOrganization(org);
            }
        }
    }, [orgId, organizationsQuery.data, setOrganization]);

    /* 
    * Create Project
    */
    const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
    const { mutateAsync: createProjectAsync } = useCreateProject();
    const onSubmit = (data: AddProjectModalSchemaType) => {
        createProjectAsync({
            name: data.name,
            organisation_id: organization?.id as number,
            challenge: data.challenge,
            crm_system: data.crm_system || DEFAULT_CRM_SYSTEM,
            crm_id: data.crm_id ? Number(data.crm_id) : null,
            crm_project_id: data.crm_project_id ? Number(data.crm_project_id) : null,
        }).then((response) => {
            SuccessPopup("Project Created Successfully")
            navigate(`/organization/${organization?.id}/project/${response.data?.id}/manage-project`);
            refetchProjectsList()
        }).catch((err) => {
            ErrorPopup("Error creating project")
            console.error(err)
        }).finally(() => {
            setOpenAddProjectModal(false)
        })
    }
    /* 
    * Create Organization
    */
    const [openCreateOrganizationModal, setOpenCreateOrganizationModal] = useState(false);
    const { mutateAsync: createOrganizationAsync } = useCreateOrganization();
    const onCreateOrganization = (data: OrganizationFormSchemaType) => {
        createOrganizationAsync({...data, crm_connections: data.crm_connections?.length ? data.crm_connections : [DEFAULT_CRM_SYSTEM]})
            .then((response) => {
                const orgId = response?.[0]?.id;
                if (orgId) {
                    navigate(`/organization/${orgId}`);
                    SuccessPopup("Organization Created Successfully");
                } else {
                    ErrorPopup("Error creating organization");
                }
                organizationsQuery.refetch()
            }).catch((err) => {
                ErrorPopup("Error creating organization")
                console.error(err)
            }).finally(() => {
                setOpenCreateOrganizationModal(false)
            })
    }
    /* 
    * MISCELLANEOUS
    */
    const { pathname } = useLocation();
    const segments = pathname.split('/');
    const lastSegmentOfURL = useMemo(() => segments[segments.length - 1], [segments])

    const [showCrmModal, setShowCrmModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Exclude<ReturnType<typeof useProjectsList>['data'], undefined>[0] | null>(null);
    const onOpenProjectCrmModal = (project: Exclude<ReturnType<typeof useProjectsList>['data'], undefined>[0]) => {
        setShowCrmModal(true);
        setSelectedProject(project);
    }
    const onCloseProjectCrmModal = () => {
        setShowCrmModal(false);
        setSelectedProject(null);
    }

    return (
        <>
            <CreateOrganizationModal
                open={openCreateOrganizationModal}
                onOpenChange={setOpenCreateOrganizationModal}
                onSubmitHandler={onCreateOrganization}
            />
            <AddProjectModal
                open={openAddProjectModal}
                setOpen={setOpenAddProjectModal}
                onSubmit={onSubmit}
            />
            <ProjectCrmModal
                show={showCrmModal}
                onClose={onCloseProjectCrmModal}
                project={selectedProject}
            />


            {['questions'].includes(lastSegmentOfURL) ? (
                <SidebarQuestionPage isFetchingProjectsList={isFetchingProjectsList} projects={sortedProjects || []} />
            ) : (
                <Sidebar className="pb-2 pr-1" name={SIDEBAR_NAMES.APP_SIDEBAR}>
                    <SidebarHeader className="px-4">
                        <div className="h-navbar"></div>
                        <p className="text-xs font-medium text-sidebar-foreground/70">Organization</p>
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger className="flex items-center justify-between w-full" asChild>
                                <Button variant='outline'>
                                    <p>{organization?.id === 'external' ? "External Projects" : organization ? organization?.name : 'Choose Organization'}</p>
                                    <ChevronsUpDown size={16} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-full max-w-full" side="right">
                                <DropdownMenuLabel>
                                    Choose organization
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => {
                                    setOrganization({ id: 'external' })
                                    setProject(null)
                                    // TODO: Create a separate page for external projects
                                    navigate(`/organization/external`)
                                }}>
                                    External Projects
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {organizationsQuery.data?.map(org => (
                                    <DropdownMenuItem key={org.id} onClick={() => {
                                        setOrganization(org)
                                        setProject(null)
                                        navigate(`/organization/${org.id}`)
                                    }}>
                                        {org.name}
                                    </DropdownMenuItem>
                                ))}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => {
                                    setOpenCreateOrganizationModal(true)
                                }}>
                                    <div className="flex items-center justify-between">
                                        <Plus size={16} />
                                        <span>Create new organization</span>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarHeader>
                    <SidebarContent className="h-full wissp-scroll-box">
                        {organization ? (
                            <>
                                <SidebarMenu>
                                    {String(organization?.id) !== 'external' && (
                                        <Collapsible defaultOpen className="group/collapsible">
                                            <SidebarGroup>
                                                <SidebarGroupLabel asChild>
                                                    <CollapsibleTrigger asChild>
                                                        <TooltipButton
                                                            variant="ghost"
                                                            size='action-sm'
                                                            tooltipContent={"Show/hide projects"}
                                                            className='w-full'
                                                            tooltipContentProps={{
                                                                align: 'end',
                                                            }}
                                                        >
                                                            Organization Settings
                                                            <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" size={20} />
                                                        </TooltipButton>
                                                    </CollapsibleTrigger>
                                                </SidebarGroupLabel>
                                                <CollapsibleContent>
                                                    <SidebarGroupContent>
                                                        <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'manage-organization'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization`}>
                                                                Manage Organization / CRM
                                                            </Link>
                                                        </SidebarMenuButton>

                                                        <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'projects'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization/projects`}>
                                                                Manage <OrganizationTypeWordingResolver capitalize plural />
                                                            </Link>
                                                        </SidebarMenuButton>
                                                        {/* <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'survey-bank-old'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization/survey-bank-old`}>
                                                                Manage Survey Templates (OLD)
                                                            </Link>
                                                        </SidebarMenuButton> */}
                                                        <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'survey-bank'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization/survey-bank`}>
                                                                Manage Survey Templates
                                                            </Link>
                                                        </SidebarMenuButton>

                                                        <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'questions-bank'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization/questions-bank`}>
                                                                Manage Question Templates
                                                            </Link>
                                                        </SidebarMenuButton>
                                                        <SidebarMenuButton asChild isActive={pathname.split('/').at(-1) === 'organization-members'}>
                                                            <Link to={`/organization/${organization?.id}/manage-organization/organization-members`}>
                                                                Manage Members / Invitations
                                                            </Link>
                                                        </SidebarMenuButton>
                                                    </SidebarGroupContent>
                                                </CollapsibleContent>
                                            </SidebarGroup>
                                        </Collapsible>
                                    )}
                                    <Collapsible defaultOpen className="group/collapsible">
                                        <SidebarGroup>
                                            <SidebarGroupLabel>
                                                {/* Projects List */}
                                                <OrganizationTypeWordingResolver capitalize plural /> List
                                                {String(organization?.id) !== 'external' && (
                                                    <SidebarGroupAction className="right-16" onClick={() => setOpenAddProjectModal(true)}>
                                                        <TooltipButton
                                                            variant="ghost"
                                                            size='action-sm'
                                                            tooltipContent="Add project"
                                                        >
                                                            <Plus size={16} />
                                                        </TooltipButton>
                                                    </SidebarGroupAction>
                                                )}

                                                <SidebarGroupAction
                                                    className="right-10"
                                                    asChild
                                                    onClick={() => nextOption()}
                                                >
                                                    <TooltipButton
                                                        size='action-sm'
                                                        variant='ghost'
                                                        tooltipContent={`Sort by ${projectsSortByLabel}`}
                                                    >
                                                        {ProjectsSortByIcon}
                                                    </TooltipButton>
                                                </SidebarGroupAction>

                                                <SidebarGroupAction className="right-4">
                                                    <CollapsibleTrigger asChild>
                                                        <TooltipButton
                                                            variant="ghost"
                                                            size='action-sm'
                                                            tooltipContent={"Show/hide projects"}
                                                        >
                                                            <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" size={20} />
                                                        </TooltipButton>
                                                    </CollapsibleTrigger>
                                                </SidebarGroupAction>
                                            </SidebarGroupLabel>
                                            <CollapsibleContent>
                                                <SidebarGroupContent>
                                                    {(() => {
                                                        if (isFetchingProjectsList) {
                                                            return (
                                                                <div className="flex items-center justify-center h-10">
                                                                    <Loader className="[animation-duration:1.5s] animate-spin" size={20} />
                                                                </div>
                                                            )
                                                        } else {
                                                            if (projectsListError) {
                                                                return (
                                                                    <div className="flex items-center justify-center h-10">
                                                                        <p className="text-center">Error fetching <OrganizationTypeWordingResolver capitalize plural /></p>
                                                                    </div>
                                                                )
                                                            } else if (sortedProjects?.length === 0) {
                                                                return (
                                                                    <div className="flex items-center justify-center h-10">
                                                                        <p className="text-center">No <OrganizationTypeWordingResolver plural /> found</p>
                                                                    </div>
                                                                )
                                                            } else {
                                                                return (
                                                                    <>
                                                                        {sortedProjects?.map(project => (
                                                                            <SidebarProject
                                                                                key={project.id}
                                                                                project={project}
                                                                                onOpenCrmModal={onOpenProjectCrmModal}
                                                                            />
                                                                        ))}
                                                                    </>
                                                                )
                                                            }
                                                        }
                                                    })()}
                                                </SidebarGroupContent>
                                            </CollapsibleContent>
                                        </SidebarGroup>
                                    </Collapsible>
                                </SidebarMenu>
                            </>
                        ) : (
                            <div className="grid flex-1 place-items-center">
                                <p>Select Organization First</p>
                            </div>
                        )}
                    </SidebarContent>
                </Sidebar>
            )}
        </>
    )
}


type SidebarProjectProps = {
    project: Exclude<ReturnType<typeof useProjectsList>['data'], undefined>[0];
    onOpenCrmModal: (project: Exclude<ReturnType<typeof useProjectsList>['data'], undefined>[0]) => void;
}

function SidebarProject({ project, onOpenCrmModal }: SidebarProjectProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { setProject } = useProjectStore();
    const { setSurvey } = useSurveyStore()
    const { data: surveys, isLoading: isLoadingSurveys, error: surveysError } = useGetSurveysByProjectId({
        projectId: project.id,
    });
    const { pathname } = useLocation();

    const { mutateAsync: deleteProject } = useDeleteProject();

    const deleteProjectHandler = async (projectId: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this project? Once deleted, you will not be able to recover this project' });
        if (!userDecision.isConfirmed) return;
        try {
            await deleteProject(projectId);
            SuccessPopup("Project deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting project");
        }
    };

    const { mutateAsync: deleteSurvey } = useDeleteSurvey();

    const deleteSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this survey? Once deleted, you will not be able to recover this survey' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteSurvey(survey_id);
            SuccessPopup("Survey deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting survey");
        }
    };

    const openCrmModalHandler = () => {
        setDropdownOpen(false);
        setTimeout(() => onOpenCrmModal(project), 0); // Delay to avoid focus trap conflict
    }
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    return (
        <>
            <ConfirmByTypingTextDialog
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                onSubmit={() => {
                    deleteProjectHandler(project.id)
                }}
                confirmText={project.name!}
            />

            <Collapsible className="group/collapsible-project" defaultOpen={pathname.includes(project.id.toString())}>
                <SidebarMenuItem>
                    <SidebarMenuButton
                        onClick={() => setProject(project)}
                        asChild
                        isActive={pathname.includes(project.id.toString())}
                    >
                        <Link to={`/organization/${project.organisation_id}/project/${project.id}/manage-project`} className="max-w-[225px] md:max-w-[310px] overflow-hidden text-ellipsis whitespace-nowrap block">
                            {project.archived && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Archive size={16} />
                                        </TooltipTrigger>
                                        <TooltipContent>Archived</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}<span>{project.name}</span>
                        </Link>
                    </SidebarMenuButton>

                    <SidebarMenuAction asChild className="right-14">
                        <CollapsibleTrigger asChild>
                            <TooltipButton
                                variant="ghost"
                                size='action-sm'
                                tooltipContent="Add survey"
                            >
                                <Link to={`/organization/${project.organisation_id}/project/${project.id}/manage-project/add-survey`}>
                                    <FilePlus2 size={16} />
                                </Link>
                            </TooltipButton>
                        </CollapsibleTrigger>
                    </SidebarMenuAction>

                    <SidebarMenuAction className="right-8">
                        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                            <DropdownMenuTrigger className="text-primary" asChild>
                                <MoreHorizontal size={16} />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" className="max-w-[300px]">
                                <DropdownMenuLabel>
                                    <p className="text-xs font-normal">Manager: {project.manager?.first_name + " " + project.manager?.last_name}</p>
                                    <p className="text-xs line-clamp-3 text-ellipsis">{project.challenge}</p>
                                </DropdownMenuLabel>

                                <DropdownMenuSeparator />

                                <DropdownMenuItem>
                                    <Link className="w-full text-xs" to={`/organization/${project.organisation_id}/project/${project.id}/manage-project/members`}>
                                        Manage Members / Invitations
                                    </Link>
                                </DropdownMenuItem>

                                <DropdownMenuItem>
                                    <Link className="w-full text-xs" to={`/organization/${project.organisation_id}/project/${project.id}/manage-project/edit`}>
                                        Edit
                                    </Link>
                                </DropdownMenuItem>

                                <DropdownMenuItem>
                                    <Button variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal" onClick={openCrmModalHandler}>
                                        Crm Connection
                                    </Button>
                                </DropdownMenuItem>

                                <DropdownMenuItem>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start w-full h-auto p-0 font-normal"
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            setIsDeleteDialogOpen(true);
                                        }}
                                    >
                                        Delete <OrganizationTypeWordingResolver capitalize />
                                    </Button>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarMenuAction>

                    <SidebarMenuAction className="right-2" asChild>
                        <CollapsibleTrigger asChild>
                            <TooltipButton
                                variant="ghost"
                                size='action-sm'
                                tooltipContent={"Show/hide surveys"}
                                asChild
                            >
                                <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible-project:rotate-180" size={16} />
                            </TooltipButton>
                        </CollapsibleTrigger>
                    </SidebarMenuAction>

                    <CollapsibleContent>
                        <SidebarMenuSub className="px-0 mx-0 ps-2.5 ms-2.5">
                            {(() => {
                                if (isLoadingSurveys) {
                                    return (
                                        <div className="flex items-center justify-center h-10">
                                            <Loader className="[animation-duration:1.5s] animate-spin" size={16} />
                                        </div>
                                    )
                                } else {
                                    if (surveysError) {
                                        return (
                                            <div className="flex items-center justify-center h-10">
                                                <p className="text-center">Error fetching surveys</p>
                                            </div>
                                        )
                                    } else if (surveys?.length === 0) {
                                        return (
                                            <div className="flex items-center justify-center h-10">
                                                <p className="text-center">No surveys found</p>
                                            </div>
                                        )
                                    } else {
                                        return (
                                            <>
                                                {surveys?.map(survey => (
                                                    <SidebarMenuItem
                                                        key={survey.id}
                                                        className=""
                                                    >
                                                        <SidebarMenuButton
                                                            key={survey.id}
                                                            onClick={() => {
                                                                setSurvey(survey)
                                                            }}
                                                            asChild
                                                            isActive={pathname.includes(survey.id.toString())}
                                                        >
                                                            <Link
                                                                to={`/organization/${project.organisation_id}/project/${project.id}/survey/${survey.id}/manage-survey`}
                                                            >
                                                                <p className="w-full overflow-hidden truncate pe-12">{survey.name}</p>
                                                            </Link>
                                                        </SidebarMenuButton>

                                                        <SidebarMenuAction className='right-8'>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger className="text-primary" asChild>
                                                                    <MoreHorizontal size={16} />
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent side="right" className="max-w-[300px]">
                                                                    {survey.description && (
                                                                        <>
                                                                            <DropdownMenuLabel>
                                                                                <p className="text-xs line-clamp-3 text-ellipsis">{survey.description}</p>
                                                                            </DropdownMenuLabel>
                                                                            <DropdownMenuSeparator />
                                                                        </>
                                                                    )}


                                                                    <DropdownMenuItem>
                                                                        <Link className="w-full text-xs" to={`#`}>
                                                                            Link
                                                                        </Link>
                                                                    </DropdownMenuItem>

                                                                    <DropdownMenuItem>
                                                                        <Link className="w-full text-xs" to={`/organization/${project.organisation_id}/project/${project.id}/survey/${survey.id}/manage-survey/export`}>
                                                                            Export
                                                                        </Link>
                                                                    </DropdownMenuItem>

                                                                    <DropdownMenuItem>
                                                                        <Button variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal" onClick={() => deleteSurveyHandler(survey.id)}>
                                                                            Delete Survey
                                                                        </Button>
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </SidebarMenuAction>
                                                    </SidebarMenuItem>
                                                ))}
                                            </>
                                        )
                                    }
                                }
                            })()}
                        </SidebarMenuSub>
                    </CollapsibleContent>
                </SidebarMenuItem>
            </Collapsible>
        </>
    )
}

type SidebarQuestionPageProps = {
    projects: Exclude<ReturnType<typeof useProjectsList>['data'], undefined> | [],
    isFetchingProjectsList: boolean,
}

function SidebarQuestionPage({
    projects,
    isFetchingProjectsList,
}: SidebarQuestionPageProps) {
    const navigate = useNavigate();
    const { orgId, projectId, surveyId } = useParams();

    const { project, setProject } = useProjectStore()

    const { pathname } = useLocation();

    const { hasAccess } = useCheckProjectAccess({
        requiredPermissions: {
            question_create: true,
            question_update: true,
            question_delete: true,
            response_validate: true,
        }
    })
    const [showConditionNotMet, setShowConditionNotMet] = useState(hasAccess)
    const [showArchivedQuestions, setShowArchivedQuestions] = useState(false)
    /*
    * Logic for questions sidebar
    */
    const segments = pathname.split('/');
    const lastSegmentOfURL = useMemo(() => segments[segments.length - 1], [segments])

    const { survey, setSurvey } = useSurveyStore()
    const { data: surveys } = useGetSurveysByProjectId({ projectId });
    useEffect(() => {
        if (['questions'].includes(lastSegmentOfURL)) {
            if (projects && (projects.length > 0) && projectId) {
                const selectedProject = projects?.find(project => project.id === projectId);
                if (selectedProject) {
                    setProject(selectedProject);
                }
            }
        }
    }, [projectId, projects, setProject, lastSegmentOfURL]);

    useEffect(() => {
        if (['questions'].includes(lastSegmentOfURL)) {
            if (surveys && (surveys.length > 0) && surveyId) {
                const selectedSurvey = surveys?.find(survey => survey.id === surveyId);
                if (selectedSurvey) {
                    setSurvey(selectedSurvey);
                }
            }
        }
    }, [surveyId, surveys, setSurvey, lastSegmentOfURL]);

    // TODO: Create questionStore and use it logic to set a store data ?
    const { data: questions, isFetching: isFetchingQuestionsBySurveyId, error: questionsError } = useGetQuestionsBySurveyId({
        surveyId: surveyId,
        isArchived: showArchivedQuestions,
        sortByCreatedAtAscending: false,
        showAll: showConditionNotMet
    });
    const {
        Icon: GroupedQuestionsSortByIcon,
        label: groupedQuestionsSortByLabel,
        nextOption: groupedQuestionsSortByNextOption,
        sortedResult: sortedGroupedQuestions,
    } = useSortByWithConfig(
        questionsSortConfig,
        questions?.grouped ?? [],
        'created_at_desc'
    );
    if (questionsError) {
        console.error(questionsError)
    }


    /*
    * Handle Add Question Dialog
    */
    const [showAddModal, setShowAddModal] = useState(false);

    const { mutateAsync: addQuestion, isPending: isPendingAddQuestion } = useAddQuestion();

    const addQuestionModalSubmitHandler = async (question: QuestionDataType) => {
        if (!surveyId) {
            ErrorPopup("Survey ID is required to add a question");
            return;
        }
        if (!orgId) {
            ErrorPopup("Organisation ID is required to add a question");
            return;
        }
        await addQuestion({
            ...question,
            survey_id: surveyId,
            crm_system: question.crm_system || DEFAULT_CRM_SYSTEM,
            organisation_id: Number(orgId),
        }).then(res => {
            SuccessPopup("Question added successfully");
            const questionId = res?.id;
            navigate(`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${questionId}`);
        }).catch((error: unknown) => {
            console.error(error)
            if (error instanceof Error) {
                ErrorPopup(error.message || "Error adding question");
            } else {
                ErrorPopup("Error adding question");
            }
        })
    }

    // * Handle Archive, Restore and Delete Question
    const { mutateAsync: archiveQuestion, } = useArchiveQuestion();
    const { mutateAsync: restoreQuestion, } = useRestoreQuestion();
    const { mutateAsync: deleteQuestion, } = useDeleteQuestion();

    const onArchiveHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive this question?", confirmButtonText: "Archive", message: 'You will be able to restore this question later' });
        if (!userDecision.isConfirmed) return;

        try {
            await archiveQuestion(question_id);
            SuccessPopup("Question archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving question");
        }
    };

    const onRestoreHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore", confirmButtonText: "Restore", message: 'Do you really want to restore this question?' });
        if (!userDecision.isConfirmed) return;

        try {
            await restoreQuestion(question_id);
            SuccessPopup("Question restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring question");
        }
    };

    const onDeleteHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this question? Once deleted, you will not be able to recover this question' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteQuestion(question_id);
            SuccessPopup("Question deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting question");
        }
    };

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState();

    const onEditHandler = (id: string) => {
        const question = questions?.questions?.find(q => q.id === id);
        if (question) {
            setSelectedQuestion(question);
            setShowEditModal(true);
        }
    }
    const onCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedQuestion(null);
    }
    const checkIfQuestionsHaveResponse = (questionData: Partial<QuestionWithResponses> | undefined) => {
        if (questionData?.response && Array.isArray(questionData.response) && questionData.response.length > 0) {
            return true;
        }
        return false
    }
    const { mutateAsync: editQuestion } = useUpdateQuestion()
    const onEditQuestionSubmitHandler = async (values: QuestionFormSchemaType) => {
        // console.log(values)
        // if (!surveyId || !orgId || !project?.crm_id) throw new Error("Missing Survey ID, Organisation ID or ProjectID.");
        await editQuestion({
            data: values
        }).then(() => {
            SuccessPopup("Question updated successfully");
            onCloseEditModal();
        }).catch((error) => {

            if (error instanceof Error) {
                ErrorPopup(error.message || "Error updating question");
            }
            else {
                ErrorPopup("Error updating question");
            }
        })
    }
    return (
        <>
            {(isFetchingProjectsList || isPendingAddQuestion) && (<LoaderWithBackdrop />)}

            <AddQuestionModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={addQuestionModalSubmitHandler}
                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={survey?.crm_config}
                questions={questions?.questions}
            />

            <EditQuestionModal
                show={showEditModal}
                onHide={onCloseEditModal}
                questionData={selectedQuestion}
                onSubmit={onEditQuestionSubmitHandler}
                isQuestionPropertiesDisabled={checkIfQuestionsHaveResponse(selectedQuestion)}
                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={survey?.crm_config}
                questions={questions?.questions}
            />

            <Sidebar className="pb-2 pr-1" name={SIDEBAR_NAMES.APP_SIDEBAR}>
                <SidebarHeader className="px-4">
                    <div className="h-navbar"></div>
                    <Link to={`/organization/${orgId}/project/${projectId}/manage-project`}>
                        <Button size="sm" variant="secondary" className="w-full">
                            <ArrowLeft size={16} className="" />
                            Back to Project
                        </Button>
                    </Link>
                    <p className="text-xs font-medium text-sidebar-foreground/70">Survey</p>
                    <DropdownMenu>
                        <DropdownMenuTrigger className="flex items-center justify-between w-full" asChild>
                            <Button variant='outline'>
                                <p className="truncate">{survey ? survey?.name : 'Choose Survey'}</p>
                                <ChevronsUpDown size={16} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full max-w-full" side="right">
                            <DropdownMenuLabel>
                                Choose survey
                            </DropdownMenuLabel>

                            <DropdownMenuSeparator />

                            {surveys?.map(survey => (
                                <DropdownMenuItem key={survey.id} onClick={() => {
                                    navigate(`/organization/${orgId}/project/${projectId}/survey/${survey.id}/manage-survey/questions`);
                                }}>
                                    {survey.name}
                                </DropdownMenuItem>
                            ))}

                            <DropdownMenuSeparator />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarHeader>

                <SidebarContent className="h-full wissp-scroll-box">

                    <SidebarGroup>
                        <SidebarGroupLabel>
                            Questions
                            <SidebarGroupAction className="right-24" onClick={() => setShowArchivedQuestions(prev => !prev)} asChild>
                                <TooltipButton
                                    variant="ghost"
                                    size='action-sm'
                                    tooltipContent={`${showArchivedQuestions ? "Hide" : "Show"} archived questions`}
                                >
                                    {showArchivedQuestions ? <EyeOff size={16} /> : <Eye size={16} />}
                                </TooltipButton>
                            </SidebarGroupAction>
                            <CheckProjectAccess>
                                <SidebarGroupAction className="right-16" onClick={() => setShowConditionNotMet(prev => !prev)} asChild>
                                    <TooltipButton
                                        variant="ghost"
                                        size='action-sm'
                                        tooltipContent={`Show questions ${showConditionNotMet ? " with conditions not met" : " with conditions met"}`}
                                    >
                                        {showConditionNotMet ? <ListTodo size={16} /> : <ListChecks size={16} />}
                                    </TooltipButton>
                                </SidebarGroupAction>
                            </CheckProjectAccess>
                            <SidebarGroupAction className="right-9" onClick={groupedQuestionsSortByNextOption}>
                                <TooltipButton
                                    variant="ghost"
                                    size='action-sm'
                                    tooltipContent={`Sort by ${groupedQuestionsSortByLabel}`}
                                >
                                    {GroupedQuestionsSortByIcon}
                                </TooltipButton>
                            </SidebarGroupAction>
                            <CheckProjectAccess question_create>
                                <SidebarGroupAction onClick={() => setShowAddModal(true)}>
                                    <TooltipButton
                                        variant="ghost"
                                        size='action-sm'
                                        tooltipContent="Add question"
                                    >
                                        <Plus size={16} />
                                    </TooltipButton>
                                </SidebarGroupAction>
                            </CheckProjectAccess>
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <div className="px-2">
                                {(() => {
                                    if (isFetchingQuestionsBySurveyId) {
                                        return (
                                            <div className="flex items-center justify-center h-10">
                                                <Loader className="[animation-duration:1.5s] animate-spin" size={20} />
                                            </div>
                                        )
                                    } else {
                                        if (questionsError) {
                                            return (
                                                <div className="flex items-center justify-center h-10">
                                                    <p className="text-center">Error fetching questions</p>
                                                </div>
                                            )
                                        } else if (questions?.questions?.length === 0) {
                                            return (
                                                <div className="flex items-center justify-center h-10">
                                                    <p className="text-center">No questions found</p>
                                                </div>
                                            )
                                        } else {
                                            return (
                                                <>
                                                    {sortedGroupedQuestions?.map((question) => (
                                                        <QuestionGroup
                                                            key={question.id}
                                                            question={question}
                                                            surveyId={surveyId!}
                                                            projectId={projectId!}
                                                            orgId={Number(orgId!)}
                                                            onEdit={onEditHandler}
                                                            onArchive={onArchiveHandler}
                                                            onRestore={onRestoreHandler}
                                                            onDelete={onDeleteHandler}
                                                            showArchived={showArchivedQuestions}
                                                        />
                                                    ))}
                                                </>
                                            )
                                        }
                                    }
                                })()}
                            </div>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>
            </Sidebar>
        </>
    )
}

function QuestionGroup({
    question,
    surveyId,
    projectId,
    orgId,
    parentConditionMet = true,
    onEdit,
    onArchive,
    onRestore,
    onDelete,
    showArchived
}: {
    question: QuestionWithDependents,
    surveyId: Tables<'survey'>['id'],
    projectId: Tables<'project'>['id'],
    orgId: Tables<'organisation'>['id'],
    parentConditionMet?: boolean,
    onEdit: (id: string) => void,
    onArchive: (id: string) => void,
    onRestore: (id: string) => void,
    onDelete: (id: string) => void,
    showArchived: boolean
}) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const questionId = useSearchParams()[0].get('questionId');
    const conditionMet = question.condition_met && parentConditionMet;

    const onEditHandler = () => {
        setDropdownOpen(false);
        onEdit(question.id);
    }
    const onArchiveHandler = () => {
        setDropdownOpen(false);
        onArchive(question.id);
    }
    const onRestoreHandler = () => {
        setDropdownOpen(false);
        onRestore(question.id);
    }
    const onDeleteHandler = () => {
        setDropdownOpen(false);
        onDelete(question.id);
    }
    if (!showArchived && question.archived === true) {
        return null; // Skip rendering if the question is not archived and showArchived is true
    }
    return (
        <div className="">
            <Link
                to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${question.id}`}
                className={cn({
                    "pointer-events-none": !conditionMet && !question.archived,
                    "opacity-50": !conditionMet && !question.archived,
                })}
            >
                <BorderBox
                    className={cn("mb-2 bg-white relative",
                        questionId === question.id ? "before:content-[''] before:absolute before:-right-3 before:w-2 before:h-2 before:bg-primary before:rounded-full" : ""
                    )}
                    size='sm'
                >
                    <p className="w-full overflow-hidden font-semibold truncate"> {question.question}</p>
                    <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>

                            <DropdownMenuTrigger className="text-primary" asChild>
                                <MoreHorizontal size={16} />
                            </DropdownMenuTrigger>

                            <DropdownMenuContent side="right" className="max-w-[300px]">
                                <CheckProjectAccess question_update>
                                    <DropdownMenuItem>
                                        <Button onClick={onEditHandler} type='button' variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal">
                                            Edit
                                        </Button>
                                    </DropdownMenuItem>
                                    {!question.archived && (
                                        <DropdownMenuItem>
                                            <Button onClick={onArchiveHandler} type='button' variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal">
                                                Archive
                                            </Button>
                                        </DropdownMenuItem>
                                    )}
                                </CheckProjectAccess>
                                {question.archived && (
                                    <>
                                        <CheckProjectAccess question_update>
                                            <DropdownMenuItem>
                                                <Button onClick={onRestoreHandler} type='button' variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal">
                                                    Restore
                                                </Button>
                                            </DropdownMenuItem>
                                        </CheckProjectAccess>
                                        <CheckProjectAccess question_delete>
                                            <DropdownMenuItem>
                                                <Button onClick={onDeleteHandler} type='button' variant="ghost" size="sm" className="justify-start w-full h-auto p-0 font-normal">
                                                    Delete
                                                </Button>
                                            </DropdownMenuItem>
                                        </CheckProjectAccess>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </BorderBox>


            </Link>

            <div className="pl-2 ml-2 conditional-group-border">
                {question.dependent_questions?.map((dependentQuestion) => (
                    <QuestionGroup
                        key={dependentQuestion.id}
                        question={dependentQuestion}
                        surveyId={surveyId}
                        projectId={projectId}
                        orgId={orgId}
                        parentConditionMet={question.condition_met}

                        onEdit={onEdit}
                        onArchive={onArchive}
                        onRestore={onRestore}
                        onDelete={onDelete}

                        showArchived={showArchived}
                    />
                ))}
            </div>
        </div>
    )
}
